// app/components/Plugins/Automations/nodes/BuySellNode.tsx
'use client'

import React, { useState, useCallback, useEffect } from 'react'
import { Handle, Position } from '@xyflow/react'
import { BuySellNodeData, NodeProps } from '../types'
import { ConnectorManager } from '../../../Connectors/ConnectorManager'

const BuySellNode: React.FC<NodeProps<BuySellNodeData>> = ({ data, isConnectable }) => {
  const [action, setAction] = useState<'BUY' | 'SELL'>(data.action || 'BUY')
  const [quantity, setQuantity] = useState(data.quantity || '1')
  const [connector, setConnector] = useState(data.connector || '')
  const [orderType, setOrderType] = useState<'MARKET' | 'LIMIT'>(data.orderType || 'MARKET')
  const [price, setPrice] = useState(data.price || '')
  const [availableBrokers, setAvailableBrokers] = useState<{id: string, name: string}[]>([])
  
  // Load available brokers
  useEffect(() => {
    const connectorManager = ConnectorManager.getInstance()
    const brokers = connectorManager.getBrokers()
    const connectedBrokers = brokers
      .filter(broker => broker.isConnected)
      .map(broker => ({ id: broker.id, name: broker.name }))
    
    setAvailableBrokers(connectedBrokers)
    
    // Set default connector if none is selected and we have connected brokers
    if (!connector && connectedBrokers.length > 0) {
      const defaultConnector = connectedBrokers[0].id
      setConnector(defaultConnector)
      data.connector = defaultConnector
    }
  }, [])
  
  const handleActionChange = useCallback(
    (evt: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = evt.target.value as 'BUY' | 'SELL'
      setAction(newValue)
      data.action = newValue
    },
    [data]
  )
  
  const handleQuantityChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = evt.target.value
      setQuantity(newValue)
      data.quantity = newValue
    },
    [data]
  )
  
  const handleConnectorChange = useCallback(
    (evt: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = evt.target.value
      setConnector(newValue)
      data.connector = newValue
    },
    [data]
  )
  
  const handleOrderTypeChange = useCallback(
    (evt: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = evt.target.value as 'MARKET' | 'LIMIT'
      setOrderType(newValue)
      data.orderType = newValue
    },
    [data]
  )
  
  const handlePriceChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = evt.target.value
      setPrice(newValue)
      data.price = newValue
    },
    [data]
  )
  
  return (
    <div className={`node buySellNode ${action === 'BUY' ? 'buyNode' : 'sellNode'}`}>
      <div className="node-header">{action}</div>
      <div className="node-content">
        <label>
          <div className="label-text">Action</div>
          <select 
            value={action} 
            onChange={handleActionChange}
            className="node-select"
          >
            <option value="BUY">BUY</option>
            <option value="SELL">SELL</option>
          </select>
        </label>
        
        <label>
          <div className="label-text">Quantity</div>
          <input 
            type="text" 
            value={quantity} 
            onChange={handleQuantityChange} 
            className="node-input"
          />
        </label>
        
        <label>
          <div className="label-text">Broker</div>
          <select
            value={connector}
            onChange={handleConnectorChange}
            className="node-select"
          >
            {availableBrokers.length === 0 && (
              <option value="">No brokers connected</option>
            )}
            {availableBrokers.map(broker => (
              <option key={broker.id} value={broker.id}>
                {broker.name}
              </option>
            ))}
          </select>
        </label>
        
        <label>
          <div className="label-text">Order Type</div>
          <select
            value={orderType}
            onChange={handleOrderTypeChange}
            className="node-select"
          >
            <option value="MARKET">Market</option>
            <option value="LIMIT">Limit</option>
          </select>
        </label>
        
        {orderType === 'LIMIT' && (
          <label>
            <div className="label-text">Price</div>
            <input
              type="text"
              value={price}
              onChange={handlePriceChange}
              className="node-input"
              placeholder="Enter limit price"
            />
          </label>
        )}
      </div>
      
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        isConnectable={isConnectable}
      />
      
      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        isConnectable={isConnectable}
      />
    </div>
  )
}

export default BuySellNode